import type { ComponentProps, ElementType, ReactNode } from 'react';

import { tv } from '../../lib/tv';
import { CATEGORY_TINT, FLOW_TINTS, type FlowCategory } from './flows-styles';

export type LibraryNodeProps = Omit<ComponentProps<'button'>, 'title'> & {
  /** Figma "Category": sets the tile's tint. */
  category: FlowCategory;
  /** The node type's icon (Phosphor), drawn 20px in the category colour. */
  icon: ElementType<{ className?: string }>;
  /** Figma "Title". */
  title: ReactNode;
  /** Figma "Description": what the node does. */
  description?: ReactNode;
  /** Figma "IO": input type → output type, e.g. "Script → Script ×N". */
  io?: ReactNode;
  /** Figma "Soon": not available yet - dimmed, labelled and not pressable. */
  soon?: boolean;
  /**
   * A pressable row (the editor's library): renders a button with hover and
   * focus ring. Otherwise a static row (the catalogue) and the button props
   * (handlers, `disabled`) are not used.
   */
  interactive?: boolean;
};

/**
 * Node entry in the library panel and the catalogue (Figma ↳ Flows ↳ Library
 * node, 153:97): 10px padding, radius/lg, gap 12; a 36px radius/md tile tinted
 * 14% with a 20px icon in the category colour; Body/Medium Strong title,
 * Caption text/secondary description, Caption text/tertiary IO line.
 */
export const libraryNodeStyles = tv({
  slots: {
    root: 'flex w-full items-start gap-3 rounded-lg p-2.5 text-left',
    tile: 'flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md',
    icon: 'size-5 shrink-0',
    text: 'flex min-w-0 flex-1 flex-col gap-0.5',
    titleRow: 'flex items-center gap-1.5',
    title: 'text-body-md-strong text-text-primary truncate',
    soon: 'text-overline text-text-tertiary uppercase',
    description: 'text-caption text-text-secondary',
    io: 'text-caption text-text-tertiary',
  },
  variants: {
    interactive: {
      true: {
        root: [
          'cursor-pointer outline-none transition-[background-color,box-shadow] duration-[120ms] ease-out',
          'hover:bg-bg-muted focus-visible:shadow-focus-ring',
          'disabled:cursor-not-allowed disabled:hover:bg-transparent',
        ],
      },
    },
    soon: {
      true: { title: 'text-text-tertiary', tile: 'opacity-60' },
    },
  },
});

export function LibraryNode({
  category,
  icon: Icon,
  title,
  description,
  io,
  soon = false,
  interactive = false,
  disabled,
  className,
  ...props
}: LibraryNodeProps) {
  const tint = FLOW_TINTS[CATEGORY_TINT[category]];
  const styles = libraryNodeStyles({ interactive, soon });
  const content = (
    <>
      <span
        data-slot="library-node-tile"
        className={styles.tile({ className: tint.soft })}
      >
        <Icon aria-hidden className={styles.icon({ className: tint.text })} />
      </span>
      <span className={styles.text()}>
        <span className={styles.titleRow()}>
          <span data-slot="library-node-title" className={styles.title()}>
            {title}
          </span>
          {soon && (
            <span data-slot="library-node-soon" className={styles.soon()}>
              Soon
            </span>
          )}
        </span>
        {description && (
          <span data-slot="library-node-description" className={styles.description()}>
            {description}
          </span>
        )}
        {io && (
          <span data-slot="library-node-io" className={styles.io()}>
            {io}
          </span>
        )}
      </span>
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        data-slot="library-node"
        data-category={category}
        disabled={disabled || soon}
        className={styles.root({ className })}
        {...props}
      >
        {content}
      </button>
    );
  }
  return (
    <div
      data-slot="library-node"
      data-category={category}
      className={styles.root({ className })}
    >
      {content}
    </div>
  );
}
